<template>
  <div>
    <page-loading v-if="!item" />
    <page-title v-if="item" :title="pageTitle" :helpLink="{ path: '/pages/station-head-help', hash: 'Station_Edit' }" />
    <b-alert v-if="item && !isEditable" show variant="info" dismissable>
      To update data about your station, please contact your station head, <a :href="'mailto:' + item.headEmail">{{item.headName}}</a>.
    </b-alert>
    <b-alert :show="item && allowGeocode && item.isOnMap && !item.longitude" variant="info" dismissable>
      This station should display on the map, but the latitude/longitude couldn't be determined based on the address.
    </b-alert>
    <b-tabs content-class="mt-3" v-model="tabIndex">
      <b-tab title="Station">
        <station-fields :item="item" v-on:submit="onSubmit" v-on:cancel="onCancel" v-if="item" :is-editable="isEditable" :allow-geocode="allowGeocode">
          <template v-slot:save>Save</template>
        </station-fields>
      </b-tab>
      <b-tab title="Collectors">
        <station-role-list :station-id="item.id" v-if="item" :is-editable="isEditable">
        </station-role-list>
      </b-tab>
      <b-tab title="Subscriptions" v-if="allowManageSubscriptions">
        <station-subscription-list :station-id="item.id" v-if="item">
        </station-subscription-list>
      </b-tab>
      <b-tab title="Allergens">
        <station-allergens :allergen-items="allergenItems" :station-id="item.id" v-if="allergenItems" :is-editable="isEditable">
        </station-allergens>
      </b-tab>
      <b-tab title="Concentration Scale">
        <station-scales :scale-set="scaleSet" :station-id="item.id" v-if="scaleSet" :is-editable="isEditable">
        </station-scales>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import CrudOperationsMixin from '../Common/CrudOperationsMixin.js'

export default {
  name: "StationEdit",
  mixins: [CrudOperationsMixin],
  props: {},
  data() {
    return {
      item: null,
      allergenItems: null,
      scaleSet: null,
      isMasterScaleSet: false,
      tabIndex: 0,
      tabIds: ['#station', '#roles', '#subscriptions', '#allergens', '#scales']
    };
  },
  computed: {
    pageTitle: function () {
      return 'Station: ' + this.item.name;
    },
    isEditable: function () {
      return this.$root.$data.authenticatedUser.roles.some(x => x == "SiteAdmin") || this.$root.$data.authenticatedUser.stationRoles.some(x => x.station.id == this.$route.params.id && x.type == "STATION_HEAD");
    },
    allowGeocode: function () {
      return this.$root.$data.authenticatedUser.roles.some(x => x == "SiteAdmin");
    },
    allowManageSubscriptions: function () {
      return this.$root.$data.authenticatedUser.roles.some(x => x == "SiteAdmin");
    }
  },
  methods: {
    load: function () {

      this.readQuery('/graphql/management', {
          query: `
            query ($id: ID) {
              station (id: $id) {
                id,
                address1,
                address2,
                city,
                concurrencyCheck,
                country,
                displayCity,
                displayType,
                faxNumber,
                headEmail,
                headName,
                isActive,
                isAddressPublic,
                isOnMap,
                latitude,
                longitude,
                name,
                name2,
                notes,
                phoneNumber,
                postalCode,
                startYear,
                state,
                website,
                scaleSet {
                  id
                }
              }
            }`,
          variables: {
            'id': this.$route.params.id
          }
        }, (data) => {
          this.item = data.station;
          this.tabIndex = this.tabIds.findIndex(tab => tab === this.$route.hash);
          this.item.scaleSetId = this.item.scaleSet.id;
          delete this.item.scaleSet;
        });

      this.readQuery('/graphql/management', {
          query: `
            query ($id: ID) {
              station (id: $id) {
                stationAllergens {
                  id,
                  allergen {
                    id
                  }
                },
                scaleSet {
                  id,
                  concurrencyCheck,
                  isMaster,
                  scales {
                    id,
                    category,
                    range1Begin,
                    range2Begin,
                    range3Begin,
                    range4Begin,
                    concurrencyCheck
                  }
                }
              },
              allergens (limit: 200) {
                id,
                name,
                commonName,
                family,
                category,
                showAtEndOfList
              }
            }`,
          variables: {
            'id': this.$route.params.id
          }
        }, (data) => {
          this.allergenItems = [];
          //data.station.stationAllergens = data.station.stationAllergens || [];
          data.allergens.forEach(a => {
            let s = data.station.stationAllergens.find(x => x.allergen.id == a.id);

            this.allergenItems.push({
              allergen: a,
              stationAllergen: s,
              isActive: s != null
            });
          });
          this.allergenItems = this.allergenItems.sort((a, b) => {
            if (a.allergen.category == 'UNIDENTIFIED')
              return 1;
            else if (b.allergen.category == 'UNIDENTIFIED')
              return -1;
            else if (a.allergen.category < b.allergen.category)
              return -1;
            else if (a.allergen.category > b.allergen.category)
              return 1;
            else if (a.allergen.showAtEndOfList)
              return 1;
            else if (b.allergen.showAtEndOfList)
              return -1;
            else if (a.allergen.commonName < b.allergen.commonName)
              return -1;
            else if (a.allergen.commonName > b.allergen.commonName)
              return 1;
            else if (a.allergen.name < b.allergen.name)
              return -1;
            else if (a.allergen.name > b.allergen.name)
              return 1;
            else
              return 0;
          });

          this.scaleSet = data.station.scaleSet;
        });        
    },
    onCancel(evt) {
      this.$router.push('/station');
    },
    onSubmit(evt) {
      this.updateQuery('/graphql/management', {
        query: `
          mutation ($id: ID!, $station: StationUpdateInputOGT!) {
            updateStation(id: $id, station: $station) {
              id,
              name
            }
          }`,
        variables: {
          'id': this.$route.params.id,
          'station': this.item
        }}, () => {
          this.$router.push('/station');
        }, null, 'Station', this.item.name);
    }
  },
  mounted () {
    this.load();
    // Use this instead of the previous line to test the "Loading" bar
    //window.setTimeout(this.load, 3000);
  }
};
</script>